import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import FormField from '../../components/ui/FormField.jsx';
import AdminAuthShell, { adminAuthInputLight, AdminAuthBackLink } from '../../components/admin/AdminAuthShell.jsx';
import api from '../../services/api.js';
import { toastError, toastSuccess } from '../../hooks/useToast.js';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [tokenState, setTokenState] = useState('loading');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { password: '', password_confirmation: '' },
  });

  const password = watch('password');

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/v1/auth/reset-password', { params: { token } });
        if (!data.success) throw new Error(data.message);
        if (!cancelled) {
          setMaskedEmail(data.data?.email || '');
          setTokenState('valid');
        }
      } catch {
        if (!cancelled) setTokenState('invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(values) {
    setSubmitting(true);
    try {
      const { data } = await api.post('/v1/auth/reset-password', {
        token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });
      if (!data.success) throw new Error(data.message || 'No se pudo actualizar la contraseña');
      setDone(true);
      toastSuccess('Contraseña actualizada', data.message);
    } catch (e) {
      const details = e.payload?.details;
      const detailMsg =
        details && typeof details === 'object'
          ? Object.values(details).find((v) => typeof v === 'string')
          : null;
      toastError('No se pudo guardar', detailMsg || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (tokenState === 'loading') {
    return (
      <AdminAuthShell title="Restablecer contraseña" subtitle="Verificando enlace…">
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand-navy" />
        </div>
      </AdminAuthShell>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <AdminAuthShell
        title="Enlace no válido"
        subtitle="El enlace expiró o ya fue utilizado"
        footer={
          <div className="space-x-4">
            <AdminAuthBackLink to="/admin/olvido-clave">Solicitar nuevo enlace</AdminAuthBackLink>
            <span className="text-white/40">·</span>
            <AdminAuthBackLink />
          </div>
        }
      >
        <p className="text-sm leading-relaxed text-slate-600">
          Solicite un nuevo correo de recuperación para continuar.
        </p>
      </AdminAuthShell>
    );
  }

  if (done) {
    return (
      <AdminAuthShell
        title="Contraseña actualizada"
        subtitle="Ya puede iniciar sesión con su nueva clave"
        footer={<AdminAuthBackLink>Iniciar sesión</AdminAuthBackLink>}
      >
        <p className="text-sm text-slate-600">
          Su contraseña fue restablecida correctamente.
        </p>
      </AdminAuthShell>
    );
  }

  return (
    <AdminAuthShell
      title="Nueva contraseña"
      subtitle={maskedEmail ? `Cuenta: ${maskedEmail}` : 'Cree una contraseña segura'}
      footer={<AdminAuthBackLink />}
    >
      <form
        className="space-y-5 [&_label>span]:text-slate-600 [&_p.text-red-400]:text-red-600"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <FormField label="Nueva contraseña" error={errors.password?.message}>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              className={`${adminAuthInputLight} pr-11`}
              {...register('password', {
                required: 'Requerido',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              ) : (
                <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              )}
            </button>
          </div>
        </FormField>

        <FormField label="Confirmar contraseña" error={errors.password_confirmation?.message}>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repita la contraseña"
              className={`${adminAuthInputLight} pr-11`}
              {...register('password_confirmation', {
                required: 'Requerido',
                validate: (value) => value === password || 'Las contraseñas no coinciden',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600"
              aria-label={showConfirm ? 'Ocultar confirmación' : 'Mostrar confirmación'}
            >
              {showConfirm ? (
                <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              ) : (
                <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              )}
            </button>
          </div>
        </FormField>

        <Button type="submit" disabled={submitting} className="w-full">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
            </>
          ) : (
            'Guardar nueva contraseña'
          )}
        </Button>
      </form>
    </AdminAuthShell>
  );
}
